import { useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Link, useSearchParams } from "react-router-dom";
import { BarChart3, Plus, RefreshCw } from "lucide-react";
import type { ProductionSummary } from "@/types/production";
import { ProductionBoard } from "@/components/production/ProductionBoard";
import { JobWorkList } from "@/components/production/JobWorkList";
import { WorkOrderList } from "@/components/production/WorkOrderList";
import { WorkOrderFormDialog } from "@/components/production/WorkOrderFormDialog";

function Tile({ label, value, hint, warn }: { label: string; value: number; hint?: string; warn?: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold tracking-tight mt-1 ${warn && value > 0 ? "text-rose-600" : ""}`}>{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

export function ProductionPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("production.create");
  const [urlParams] = useSearchParams();
  const [tab, setTab] = useState<"board" | "list" | "jobwork">(["list", "jobwork"].includes(urlParams.get("tab") || "") ? (urlParams.get("tab") as "list" | "jobwork") : "board");
  const [summary, setSummary] = useState<ProductionSummary | null>(null);
  const [dialog, setDialog] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get("/api/WorkOrder/Summary").then((r) => setSummary(unwrapObject<ProductionSummary>(r))).catch(() => setSummary(null));
  }, [reloadKey]);
  const refresh = () => setReloadKey((k) => k + 1);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Production</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Work orders moving through weaving, dyeing, finishing and the rest, against orders or for stock</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} className="h-9 gap-1.5">
            <RefreshCw className="size-3.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" asChild className="h-9 gap-1.5">
            <Link to="/production/report"><BarChart3 className="size-3.5" /> Wastage report</Link>
          </Button>
          {canCreate && (
            <Button size="sm" onClick={() => setDialog(true)} className="h-9 gap-1.5 shadow-sm">
              <Plus className="size-4" /> New work order
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Tile label="Planned" value={summary.planned} hint="Not started yet" />
          <Tile label="In progress" value={summary.in_progress} />
          <Tile label="Due this week" value={summary.due_this_week} />
          <Tile label="Past due" value={summary.past_due} hint="Work orders" warn />
          <Tile label="Late stages" value={summary.late_stages} hint="Behind their plan" warn />
        </div>
      )}

      <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit">
        {(["board", "list", "jobwork"] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={`h-8 px-4 rounded-md text-sm font-medium transition-colors ${tab === t ? "bg-background shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {t === "board" ? "Board" : t === "list" ? "Work orders" : "Job work"}
          </button>
        ))}
      </div>

      {tab === "board" ? <ProductionBoard reloadKey={reloadKey} /> : tab === "list" ? <WorkOrderList reloadKey={reloadKey} /> : <JobWorkList reloadKey={reloadKey} />}

      <WorkOrderFormDialog open={dialog} onClose={() => setDialog(false)} onSaved={refresh} />
    </div>
  );
}
